/* ==========================================================================
   Mission Image - Asian American Forward Website
   ========================================================================== */

import { preventModalScroll, allowModalScroll } from './utils/scroll-prevention.js';

import { setup3DCardHover, isGSAPLoaded } from './utils/gsap-animations.js';
import { gsap } from 'gsap';

let missionImageContainer = null;

export function initializeMissionImage() {
  console.log('🖼️ Initializing Mission image...');
  
  missionImageContainer = document.querySelector('.mission-img-container');
  
  if (!missionImageContainer) {
    console.warn('Mission image container not found');
    return;
  }
  
  // Set up click handler for expansion
  missionImageContainer.addEventListener('click', () => toggleImageExpansion());
  
  // 3D hover effects disabled for Mission image
  console.log('✨ 3D hover effects disabled for Mission image');
  
  console.log('✅ Mission image initialized');
}

/* ==========================================================================
   3D Hover Effects (GSAP)
   ========================================================================== */

function setup3DMissionImageHover(container) {
  if (!isGSAPLoaded()) {
    console.warn('GSAP not loaded, skipping 3D hover setup');
    return;
  }
  
  console.log('✨ Setting up 3D hover for Mission image');
  
  let hoverTimeout = null;
  
  container.addEventListener('mouseenter', () => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Dispatch event to pause other animations if needed
    window.dispatchEvent(new CustomEvent('pauseCardCycling'));
    
    // Enhanced hover effect (match hero cards)
    gsap.to(container, {
      rotationX: 0,
      rotationY: 0,
      z: 35, // Same lift as hero cards
      duration: 0.3,
      ease: "power2.out"
    });
  });
  
  container.addEventListener('mouseleave', () => {
    // Reset on mouse leave (match hero cards)
    gsap.to(container, {
      rotationX: 0,
      rotationY: 0,
      z: 0,
      duration: 0.4,
      ease: "power2.out"
    });
    
    // Resume other animations after delay (match hero cards)
    hoverTimeout = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('resumeCardCycling'));
    }, 1000);
  });
  
  container.addEventListener('mousemove', (e) => {
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = (e.clientX - centerX) / (rect.width / 2);
    const mouseY = (e.clientY - centerY) / (rect.height / 2);
    
    // Calculate rotation based on mouse position (match hero cards)
    const rotateX = mouseY * -5; // Max 5 degrees (same as hero cards)
    const rotateY = mouseX * 5; // Max 5 degrees (same as hero cards)
    
    // Apply 3D rotation (match hero cards)
    gsap.to(container, {
      rotationX: rotateX,
      rotationY: rotateY,
      z: 40, // Same lift as hero cards
      duration: 0.1,
      ease: "none"
    });
  });
}

/* ==========================================================================
   Fallback 3D Mouse Effect (Vanilla JS)
   ========================================================================== */

function setup3DMouseEffect(container) {
  console.log('🎨 Setting up fallback 3D mouse effect for Mission image');
  
  container.addEventListener('mouseenter', () => {
    container.style.transform = 'translateZ(35px)';
  });
  
  container.addEventListener('mouseleave', () => {
    container.style.transform = '';
  });
  
  container.addEventListener('mousemove', (e) => {
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = (e.clientX - centerX) / (rect.width / 2);
    const mouseY = (e.clientY - centerY) / (rect.height / 2);
    
    // Calculate rotation based on mouse position (match hero cards)
    const rotateX = mouseY * -5; // Max 5 degrees (same as hero cards)
    const rotateY = mouseX * 5; // Max 5 degrees (same as hero cards)
    
    // Apply transform (match hero cards)
    container.style.transform = `
      translateZ(40px) 
      rotateX(${rotateX}deg) 
      rotateY(${rotateY}deg)
    `;
  });
}

/* ==========================================================================
   Expand/Contract Functionality (Same as Hero Cards)
   ========================================================================== */

function toggleImageExpansion() {
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  const missionImg = missionImageContainer.querySelector('.mission-img');
  
  if (!modal || !modalImage || !missionImg) {
    console.warn('Modal elements not found');
    return;
  }
  
  // Check if modal is already open
  const isModalOpen = modal.style.display === 'flex';
  
  if (!isModalOpen) {
    expandImage();
  }
  // Note: We don't need to handle closing here since the modal handles it
}

function expandImage() {
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  const missionImg = missionImageContainer.querySelector('.mission-img');
  
  if (!modal || !modalImage || !missionImg) return;
  
  // Ensure this is only called for mission images, not other cards
  if (!missionImageContainer.classList.contains('mission-img-container')) {
    console.log('🚫 Ignoring non-mission image expansion attempt');
    return;
  }
  
  console.log('🔍 Expanding Mission image');
  
  // Set modal image source
  modalImage.src = missionImg.src;
  modalImage.alt = missionImg.alt;
  
  // Prevent scrolling
  preventModalScroll();
  
  // Show modal (same as hero cards)
  modal.style.display = 'flex';
  modal.setAttribute('aria-hidden', 'false');
  modalImage.focus({ preventScroll: true });
  
  // Set up close handler (same as hero cards)
  const closeModal = () => {
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
    
    // Re-enable scrolling
    allowModalScroll();
    
    missionImageContainer.focus({ preventScroll: true }); // Return focus to image container
    
    // Remove event listeners
    modal.removeEventListener('click', closeModal);
    document.removeEventListener('keydown', handleKeydown);
  };
  
  // Close modal when clicking anywhere on the modal (same as hero cards)
  modal.addEventListener('click', closeModal);
  
  // Close on Escape key (same as hero cards)
  const handleKeydown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleKeydown);
    }
  };
  
  document.addEventListener('keydown', handleKeydown);
}

/* ==========================================================================
   Reduced Motion Support
   ========================================================================== */

function checkReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Initialize with reduced motion check
if (checkReducedMotion()) {
  console.log('🎯 Reduced motion detected - Mission image animations will be minimal');
} 